<template>
  <Teleport to="body">
    <Transition name="qo-sheet">
      <div
        v-if="modelValue"
        class="qo-sheet"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('public.quickOrder.yourOrder')"
        @keydown.escape="close"
      >
        <div class="qo-sheet__backdrop" aria-hidden="true" @click="close" />

        <div class="qo-sheet__panel" ref="panelRef" @keydown="onPanelKeydown">
          <!-- Header -->
          <div class="qo-sheet__header">
            <h2 class="qo-sheet__title">{{ $t('public.quickOrder.yourOrder') }}</h2>
            <div class="qo-sheet__header-actions">
              <button
                v-if="items.length"
                type="button"
                class="qo-sheet__clear-btn"
                @click="$emit('clear-cart')"
              >
                {{ $t('public.quickOrder.clearAll') }}
              </button>
              <button
                ref="closeBtnRef"
                type="button"
                class="qo-sheet__close-btn"
                :aria-label="$t('public.quickOrder.ariaClose')"
                @click="close"
              >
                <span class="material-icons" aria-hidden="true">close</span>
              </button>
            </div>
          </div>

          <div class="qo-sheet__scroll">
            <!-- Empty state -->
            <div v-if="!items.length" class="qo-sheet__empty">
              <span class="material-icons qo-sheet__empty-icon" aria-hidden="true">shopping_cart</span>
              <p>{{ $t('public.quickOrder.cartEmpty') }}</p>
            </div>

            <template v-else>
              <!-- Cart items (key by menu_item_uuid + variant_uuid so variants are separate lines) -->
              <ul class="qo-sheet__items">
                <li v-for="cartItem in items" :key="cartLineKey(cartItem)" class="qo-sheet__item">
                  <div class="qo-sheet__item-info">
                    <span class="qo-sheet__item-name">{{ cartItem.name }}</span>
                    <span v-if="cartItem.price != null" class="qo-sheet__item-price">
                      {{ formatPrice(cartItem.price) }}
                    </span>
                  </div>
                  <div class="qo-sheet__item-controls">
                    <div class="qo-sheet__qty-group">
                      <button
                        type="button"
                        class="qo-sheet__qty-btn"
                        :style="primaryBtnStyle"
                        :aria-label="$t('public.quickOrder.ariaDecrease')"
                        @click="$emit('update-quantity', cartItem.menu_item_uuid, cartItem.quantity - 1, cartItem.variant_uuid, cartItem.modifier_uuids)"
                      >
                        <span class="material-icons" aria-hidden="true">remove</span>
                      </button>
                      <span class="qo-sheet__qty-value">{{ cartItem.quantity }}</span>
                      <button
                        type="button"
                        class="qo-sheet__qty-btn"
                        :style="primaryBtnStyle"
                        :disabled="cartItem.quantity >= 99"
                        :aria-label="$t('public.quickOrder.ariaIncrease')"
                        @click="$emit('update-quantity', cartItem.menu_item_uuid, cartItem.quantity + 1, cartItem.variant_uuid, cartItem.modifier_uuids)"
                      >
                        <span class="material-icons" aria-hidden="true">add</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      class="qo-sheet__remove-btn"
                      :aria-label="$t('public.quickOrder.ariaRemoveItem')"
                      @click="$emit('remove-item', cartItem.menu_item_uuid, cartItem.variant_uuid, cartItem.modifier_uuids)"
                    >
                      <span class="material-icons" aria-hidden="true">delete_outline</span>
                    </button>
                  </div>
                </li>
              </ul>

              <!-- Order form -->
              <form novalidate class="qo-sheet__form" @submit.prevent>
                <div class="qo-sheet__field">
                  <label for="qo-name" class="qo-sheet__label">
                    {{ $t('public.quickOrder.name') }}
                    <span class="qo-sheet__required" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="qo-name"
                    v-model="orderName"
                    type="text"
                    autocomplete="name"
                    maxlength="100"
                    class="qo-sheet__input"
                    :placeholder="$t('public.quickOrder.namePlaceholder')"
                    :aria-invalid="!!errors.name"
                    :aria-describedby="errors.name ? 'qo-name-error' : undefined"
                  />
                  <p v-if="errors.name" id="qo-name-error" class="qo-sheet__error" role="alert">{{ errors.name }}</p>
                </div>

                <div class="qo-sheet__field">
                  <div class="qo-sheet__label-row">
                    <label for="qo-notes" class="qo-sheet__label">
                      {{ $t('public.quickOrder.notes') }}
                    </label>
                    <span class="qo-sheet__char-count" aria-hidden="true">{{ orderNotes.length }}/500</span>
                  </div>
                  <textarea
                    id="qo-notes"
                    v-model="orderNotes"
                    rows="3"
                    maxlength="500"
                    class="qo-sheet__input qo-sheet__textarea"
                    :placeholder="$t('public.quickOrder.notesPlaceholder')"
                    :aria-invalid="!!errors.notes"
                    :aria-describedby="errors.notes ? 'qo-notes-error' : undefined"
                  />
                  <p v-if="errors.notes" id="qo-notes-error" class="qo-sheet__error" role="alert">{{ errors.notes }}</p>
                </div>

                <!-- Pro/VIP: API checkout (mobile app owners). Otherwise WhatsApp/SMS deep links + same API. -->
                <div v-if="checkoutOnly" class="qo-sheet__send-options qo-sheet__send-options--single">
                  <button
                    type="button"
                    class="qo-sheet__send-btn qo-sheet__send-btn--checkout"
                    :style="primaryBtnStyle"
                    @click="openCheckoutReview"
                  >
                    <span class="material-icons" aria-hidden="true">shopping_bag</span>
                    {{ $t('public.quickOrder.checkout') }}
                  </button>
                </div>
                <div v-else class="qo-sheet__send-options">
                  <button
                    v-if="quickOrderPhone"
                    type="button"
                    class="qo-sheet__send-btn qo-sheet__send-btn--whatsapp"
                    @click="submit('whatsapp')"
                  >
                    <span class="material-icons" aria-hidden="true">chat</span>
                    {{ $t('public.quickOrder.sendViaWhatsApp') }}
                  </button>
                  <button
                    v-if="quickOrderPhone"
                    type="button"
                    class="qo-sheet__send-btn qo-sheet__send-btn--sms"
                    @click="submit('sms')"
                  >
                    <span class="material-icons" aria-hidden="true">sms</span>
                    {{ $t('public.quickOrder.sendViaSMS') }}
                  </button>
                </div>
                <div
                  v-if="recaptchaEnabled && !recaptchaIsV3 && !checkoutOnly"
                  class="qo-sheet__field qo-sheet__field--recaptcha"
                >
                  <div ref="qoRecaptchaContainerRef" class="qo-sheet__recaptcha" aria-label="reCAPTCHA" />
                  <p
                    v-if="errors.captcha_token"
                    id="qo-captcha-error"
                    class="qo-sheet__error"
                    role="alert"
                  >
                    {{ errors.captcha_token }}
                  </p>
                </div>
                <p v-if="submitError" class="qo-sheet__error qo-sheet__submit-error" role="alert">{{ submitError }}</p>
              </form>
            </template>
          </div>
        </div>

        <!-- Order summary + confirm (Pro/VIP app checkout) -->
        <div
          v-if="checkoutReviewOpen && items.length"
          class="qo-checkout-review"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qo-checkout-summary-title"
          @keydown.escape.prevent="closeCheckoutReview"
        >
          <div class="qo-checkout-review__backdrop" aria-hidden="true" @click="closeCheckoutReview" />
          <div class="qo-checkout-review__panel" tabindex="-1" ref="checkoutPanelRef">
            <div class="qo-checkout-review__header">
              <h3 id="qo-checkout-summary-title" class="qo-checkout-review__title">
                {{ $t('public.quickOrder.orderSummary') }}
              </h3>
              <button
                type="button"
                class="qo-checkout-review__close"
                :aria-label="$t('public.quickOrder.ariaCloseSummary')"
                @click="closeCheckoutReview"
              >
                <span class="material-icons" aria-hidden="true">close</span>
              </button>
            </div>
            <ul class="qo-checkout-review__lines">
              <li v-for="cartItem in items" :key="'r-' + cartLineKey(cartItem)" class="qo-checkout-review__line">
                <div class="qo-checkout-review__line-main">
                  <span class="qo-checkout-review__qty">{{ cartItem.quantity }}×</span>
                  <span class="qo-checkout-review__name">{{ cartItem.name }}</span>
                </div>
                <span v-if="lineSubtotal(cartItem) != null" class="qo-checkout-review__line-price">
                  {{ formatPrice(lineSubtotal(cartItem)) }}
                </span>
              </li>
            </ul>
            <div v-if="cartSubtotal != null" class="qo-checkout-review__subtotal">
              <span>{{ $t('public.quickOrder.subtotal') }}</span>
              <strong>{{ formatPrice(cartSubtotal) }}</strong>
            </div>
            <div v-if="checkoutOnly && cartSubtotal != null" class="qo-checkout-review__total-line">
              <span>{{ $t('public.quickOrder.total') }}</span>
              <strong>{{ formatPrice(checkoutGrandTotal) }}</strong>
            </div>
            <div
              v-if="recaptchaEnabled && !recaptchaIsV3 && checkoutOnly"
              class="qo-checkout-review__field qo-checkout-review__field--recaptcha"
            >
              <div ref="qoCheckoutRecaptchaRef" class="qo-sheet__recaptcha" aria-label="reCAPTCHA" />
              <p
                v-if="errors.captcha_token"
                id="qo-checkout-captcha-error"
                class="qo-sheet__error"
                role="alert"
              >
                {{ errors.captcha_token }}
              </p>
            </div>
            <p v-if="checkoutError" class="qo-sheet__error" role="alert">{{ checkoutError }}</p>
            <button
              type="button"
              class="qo-checkout-review__confirm"
              :style="primaryBtnStyle"
              :disabled="checkoutSubmitting"
              @click="confirmAppCheckout"
            >
              {{ checkoutSubmitting ? $t('public.quickOrder.checkoutSubmitting') : $t('public.quickOrder.confirmCheckout') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { quickOrderService } from '@/services'
import { normalizeApiError } from '@/services/api'
import { formatPriceDisplay } from '@/utils/format.js'
import { normalizeModifierUuidList } from '@/models/MenuItemModifier.js'
import { useScrollLock } from '@/composables/useScrollLock'
import { useRecaptcha } from '@/composables/useRecaptcha'

const { t } = useI18n()
const { lock: lockScroll, unlock: unlockScroll } = useScrollLock()
const {
  recaptchaEnabled,
  recaptchaIsV3,
  loadScript,
  renderWidget,
  getToken: getRecaptchaToken,
  executeToken: executeRecaptchaToken,
  reset: resetRecaptcha,
} = useRecaptcha()
const qoRecaptchaContainerRef = ref(null)
const qoCheckoutRecaptchaRef = ref(null)

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  slug: { type: String, required: true },
  quickOrderPhone: { type: String, default: null },
  /** Pro/VIP owners: hide WhatsApp/SMS; use Checkout → API (dine-in only on public until pickup is ready). */
  checkoutOnly: { type: Boolean, default: false },
  primaryColor: { type: String, default: '' },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
})

const emit = defineEmits([
  'update:modelValue',
  'clear-cart',
  'remove-item',
  'update-quantity',
  'order-sent',
])

const panelRef = ref(null)
const closeBtnRef = ref(null)
let triggerRef = null
/** Public quick order is dine-in only (pickup UI/API flow deferred). */
const PUBLIC_QUICK_ORDER_FULFILLMENT = 'dine_in'

const orderName = ref('')
const orderNotes = ref('')
const errors = ref({})
const submitError = ref('')
const checkoutReviewOpen = ref(false)
const checkoutSubmitting = ref(false)
const checkoutError = ref('')
const checkoutPanelRef = ref(null)

const primary = computed(() => props.primaryColor || '#2563eb')
const primaryBtnStyle = computed(() => ({
  backgroundColor: primary.value,
  borderColor: primary.value,
  color: '#fff',
}))

function cartLineKey(cartItem) {
  const mods = JSON.stringify(normalizeModifierUuidList(cartItem.modifier_uuids))
  return `${cartItem.menu_item_uuid}\n${cartItem.variant_uuid ?? ''}\n${mods}`
}

function formatPrice(price) {
  const cur = props.currency || 'USD'
  const show = props.showCurrency !== false
  return formatPriceDisplay(Number(price), cur, show)
}

function lineSubtotal(cartItem) {
  const p = cartItem?.price
  if (p == null || Number.isNaN(Number(p))) return null
  const q = Math.max(1, Number(cartItem.quantity) || 1)
  return Number(p) * q
}

const cartSubtotal = computed(() => {
  let sum = 0
  for (const i of props.items) {
    const line = lineSubtotal(i)
    if (line == null) return null
    sum += line
  }
  return sum
})

const checkoutGrandTotal = computed(() => cartSubtotal.value)

function openCheckoutReview() {
  submitError.value = ''
  checkoutError.value = ''
  checkoutReviewOpen.value = true
  nextTick(() => checkoutPanelRef.value?.focus())
}

function closeCheckoutReview() {
  checkoutReviewOpen.value = false
  checkoutError.value = ''
}

async function confirmAppCheckout() {
  checkoutError.value = ''
  if (!props.checkoutOnly || !props.items.length) return

  const baseErrs = {}
  const name = (orderName.value ?? '').trim()
  if (!name) baseErrs.name = t('public.quickOrder.nameRequired')
  else if (name.length > 100) baseErrs.name = t('public.quickOrder.nameMaxLength')
  const notesLen = (orderNotes.value ?? '').length
  if (notesLen > 500) baseErrs.notes = t('public.quickOrder.notesMaxLength')

  errors.value = baseErrs
  if (Object.keys(baseErrs).length > 0) return

  if (recaptchaEnabled.value && !recaptchaIsV3.value && !getRecaptchaToken()) {
    errors.value = { ...errors.value, captcha_token: t('public.captchaRequired') }
    return
  }

  checkoutSubmitting.value = true
  try {
    let captchaToken = ''
    if (recaptchaEnabled.value) {
      captchaToken = recaptchaIsV3.value
        ? await executeRecaptchaToken('quick_order')
        : getRecaptchaToken()
      if (!captchaToken) {
        checkoutError.value = t('public.captchaRequired')
        errors.value = { ...errors.value, captcha_token: t('public.captchaRequired') }
        if (!recaptchaIsV3.value) resetRecaptcha()
        return
      }
    }
    const nameTrim = orderName.value.trim()
    const notes = (orderNotes.value ?? '').trim()
    const payload = {
      name: nameTrim,
      notes,
      items: props.items.map((i) => {
        const mods = normalizeModifierUuidList(i.modifier_uuids)
        return {
          menu_item_uuid: i.menu_item_uuid,
          quantity: i.quantity,
          ...(i.variant_uuid ? { variant_uuid: i.variant_uuid } : {}),
          ...(mods.length ? { modifier_uuids: mods } : {}),
        }
      }),
      medium: 'app',
      fulfillment_type: PUBLIC_QUICK_ORDER_FULFILLMENT,
      ...(captchaToken ? { captcha_token: captchaToken } : {}),
    }
    await quickOrderService.submitOrder(props.slug, payload)
    orderName.value = ''
    orderNotes.value = ''
    errors.value = {}
    checkoutReviewOpen.value = false
    if (recaptchaEnabled.value) resetRecaptcha()
    emit('order-sent')
    close()
  } catch (e) {
    const data = e?.response?.data
    const captchaErr = data?.errors?.captcha_token?.[0]
    if (captchaErr && recaptchaEnabled.value) {
      errors.value = { ...errors.value, captcha_token: captchaErr }
      resetRecaptcha()
    }
    checkoutError.value = normalizeApiError(e).message || t('public.quickOrder.checkoutFailed')
  } finally {
    checkoutSubmitting.value = false
  }
}

function validate() {
  const errs = {}
  const name = (orderName.value ?? '').trim()
  if (!name) errs.name = t('public.quickOrder.nameRequired')
  else if (name.length > 100) errs.name = t('public.quickOrder.nameMaxLength')

  const notesLen = (orderNotes.value ?? '').length
  if (notesLen > 500) errs.notes = t('public.quickOrder.notesMaxLength')

  if (recaptchaEnabled.value && !recaptchaIsV3.value && !props.checkoutOnly) {
    if (!getRecaptchaToken()) {
      errs.captcha_token = t('public.captchaRequired')
    }
  }

  errors.value = errs
  return Object.keys(errs).length === 0
}

/** Build order text in same format as backend (for deep links; no API response needed before opening link). */
function buildOrderText(name, notes, items) {
  const pad = (n) => String(n).padStart(2, '0')
  const d = new Date()
  const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  const fulfillmentLabel = t('public.quickOrder.fulfillmentDineIn')
  const lines = [
    'Sent from QRestoo',
    `Date: ${dateStr}`,
    '',
    `Name: ${name}`,
    `Fulfillment: ${fulfillmentLabel}`,
    '',
    `Notes: ${notes}`,
    '',
  ]
  for (const item of items) {
    lines.push(`${item.quantity} x ${item.name}`)
  }
  return lines.join('\n')
}

function buildDeepLink(medium, phone, orderText) {
  const encoded = encodeURIComponent(orderText)
  const cleanPhone = (phone ?? '').replace(/[^+\d]/g, '')
  if (medium === 'whatsapp') return `https://wa.me/${cleanPhone}?text=${encoded}`
  if (medium === 'sms') return `sms:${cleanPhone}?body=${encoded}`
  return null
}

/**
 * Open wa.me / sms: in a new tab after async work (e.g. reCAPTCHA v3).
 * Do not use noopener on the placeholder window.open — Chromium returns null (no WindowProxy) but
 * still opens about:blank, so we cannot assign location and the tab stays blank.
 */
function openDeepLinkInNewTab(link) {
  const a = document.createElement('a')
  a.href = link
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  a.style.setProperty('position', 'fixed')
  a.style.setProperty('left', '-9999px')
  document.body.appendChild(a)
  a.click()
  a.remove()
}

async function submit(medium) {
  submitError.value = ''
  if (!validate()) return
  if (!props.items.length) return

  const useBlankFirst =
    props.quickOrderPhone &&
    (medium === 'whatsapp' || medium === 'sms') &&
    recaptchaEnabled.value &&
    recaptchaIsV3.value
  let placeholder = null
  if (useBlankFirst) {
    // Must not pass noopener: Chromium returns null (no reference) yet still opens a blank tab,
    // so after await recaptcha we cannot navigate it and guests see a stuck blank tab.
    placeholder = window.open('about:blank', '_blank')
  }

  let captchaToken = ''
  if (recaptchaEnabled.value) {
    captchaToken = recaptchaIsV3.value
      ? await executeRecaptchaToken('quick_order')
      : getRecaptchaToken()
    if (!captchaToken) {
      submitError.value = t('public.captchaRequired')
      errors.value = { ...errors.value, captcha_token: t('public.captchaRequired') }
      placeholder?.close()
      resetRecaptcha()
      return
    }
  }

  const name = orderName.value.trim()
  const notes = (orderNotes.value ?? '').trim()
  const payload = {
    name,
    notes,
    items: props.items.map((i) => {
      const mods = normalizeModifierUuidList(i.modifier_uuids)
      return {
        menu_item_uuid: i.menu_item_uuid,
        quantity: i.quantity,
        ...(i.variant_uuid ? { variant_uuid: i.variant_uuid } : {}),
        ...(mods.length ? { modifier_uuids: mods } : {}),
      }
    }),
    medium,
    fulfillment_type: PUBLIC_QUICK_ORDER_FULFILLMENT,
    ...(captchaToken ? { captcha_token: captchaToken } : {}),
  }

  const orderText = buildOrderText(name, notes, props.items)
  const link = buildDeepLink(medium, props.quickOrderPhone, orderText)
  if (link) {
    if (placeholder) {
      try {
        placeholder.location.replace(link)
      } catch {
        try {
          placeholder.location.href = link
        } catch {
          openDeepLinkInNewTab(link)
        }
      }
    } else {
      try {
        window.open(link, '_blank', 'noopener,noreferrer')
      } catch {
        openDeepLinkInNewTab(link)
      }
    }
  } else {
    placeholder?.close()
  }

  try {
    await quickOrderService.submitOrder(props.slug, payload)
  } catch (e) {
    const data = e?.response?.data
    const captchaErr = data?.errors?.captcha_token?.[0]
    if (captchaErr && recaptchaEnabled.value) {
      errors.value = { ...errors.value, captcha_token: captchaErr }
      resetRecaptcha()
    }
    submitError.value = normalizeApiError(e).message || t('public.quickOrder.checkoutFailed')
    return
  }

  orderName.value = ''
  orderNotes.value = ''
  errors.value = {}
  if (recaptchaEnabled.value) resetRecaptcha()
  emit('order-sent')
  close()
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusables(container) {
  if (!container) return []
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent != null && !el.hasAttribute('aria-hidden')
  )
}

function onPanelKeydown(e) {
  if (e.key !== 'Tab' || !props.modelValue || !panelRef.value) return
  const focusables = getFocusables(panelRef.value)
  if (focusables.length === 0) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault()
      last.focus()
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

function close() {
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (open) => {
  if (open) {
    triggerRef = document.activeElement
    submitError.value = ''
    checkoutError.value = ''
    checkoutReviewOpen.value = false
    errors.value = {}
    lockScroll()
    nextTick(() => closeBtnRef.value?.focus())
  } else {
    checkoutReviewOpen.value = false
    if (recaptchaEnabled.value && !recaptchaIsV3.value) {
      resetRecaptcha()
    }
    unlockScroll()
    nextTick(() => {
      if (triggerRef && typeof triggerRef.focus === 'function') {
        triggerRef.focus()
      }
      triggerRef = null
    })
  }
})

watch(
  () => props.modelValue,
  (open) => {
    if (!open || !recaptchaEnabled.value || !props.items.length) return
    loadScript().catch(() => {})
    if (recaptchaIsV3.value || props.checkoutOnly) return
    nextTick(() => {
      nextTick(() => {
        const el = qoRecaptchaContainerRef.value
        if (el) renderWidget(el).catch(() => {})
      })
    })
  },
)

watch(checkoutReviewOpen, (open) => {
  if (!open || !recaptchaEnabled.value || recaptchaIsV3.value || !props.checkoutOnly) return
  if (!props.modelValue || !props.items.length) return
  loadScript()
    .then(() => {
      nextTick(() => {
        nextTick(() => {
          const el = qoCheckoutRecaptchaRef.value
          if (el) renderWidget(el).catch(() => {})
        })
      })
    })
    .catch(() => {})
})

onBeforeUnmount(() => {
  unlockScroll()
})
</script>

<style scoped>
.qo-sheet {
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  min-height: 0;
  overflow: hidden;
}

@media (min-width: 768px) {
  .qo-sheet {
    justify-content: center;
  }
}

.qo-sheet__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.qo-sheet__panel {
  position: relative;
  width: 100%;
  max-height: min(85vh, 85dvh);
  background: #fff;
  border-radius: 1rem 1rem 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

@media (min-width: 768px) {
  .qo-sheet__panel {
    max-width: 480px;
    border-radius: 1rem;
    max-height: min(90vh, 90dvh);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  }
}

.qo-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.qo-sheet__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
}

.qo-sheet__header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.qo-sheet__clear-btn {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}

.qo-sheet__clear-btn:hover {
  text-decoration: underline;
}

.qo-sheet__close-btn {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f1f5f9;
  border-radius: 0.5rem;
  color: #334155;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
}

.qo-sheet__close-btn:hover {
  background: #e2e8f0;
}

.qo-sheet__close-btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.qo-sheet__scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
}

.qo-sheet__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #94a3b8;
}

.qo-sheet__empty-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.qo-sheet__items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.qo-sheet__item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.qo-sheet__item:last-child {
  border-bottom: none;
}

.qo-sheet__item-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.qo-sheet__item-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: #0f172a;
}

.qo-sheet__item-price {
  font-weight: 600;
  font-size: 0.875rem;
  color: #64748b;
  white-space: nowrap;
}

.qo-sheet__item-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.qo-sheet__qty-group {
  display: flex;
  align-items: center;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.qo-sheet__qty-btn {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.15s;
}

.qo-sheet__qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qo-sheet__qty-btn:hover:not(:disabled) {
  opacity: 0.85;
}

.qo-sheet__qty-btn .material-icons {
  font-size: 1.125rem;
}

.qo-sheet__qty-value {
  min-width: 2rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: #0f172a;
  padding: 0 0.125rem;
}

.qo-sheet__remove-btn {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: #ef4444;
  cursor: pointer;
  border-radius: 0.375rem;
  padding: 0;
  transition: background 0.15s;
}

.qo-sheet__remove-btn:hover {
  background: #fef2f2;
}

.qo-sheet__form {
  padding: 1rem 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.qo-sheet__field {
  margin-bottom: 1rem;
}

.qo-sheet__recaptcha {
  min-height: 78px;
}

.qo-sheet__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.qo-sheet__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.375rem;
}

.qo-sheet__required {
  color: #ef4444;
}

.qo-sheet__char-count {
  font-size: 0.75rem;
  color: #94a3b8;
}

.qo-sheet__input {
  width: 100%;
  min-height: 44px;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  color: #0f172a;
  background: #fff;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.qo-sheet__input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.qo-sheet__textarea {
  resize: none;
  min-height: 80px;
}

.qo-sheet__error {
  margin: 0.375rem 0 0;
  font-size: 0.8125rem;
  color: #ef4444;
}

.qo-sheet__submit-error {
  margin-top: 0.75rem;
  text-align: center;
}

.qo-sheet__send-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.qo-sheet__send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 48px;
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: opacity 0.15s;
}

.qo-sheet__send-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.qo-sheet__send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qo-sheet__send-btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.qo-sheet__send-btn .material-icons {
  font-size: 1.25rem;
}

.qo-sheet__send-btn--whatsapp {
  background-color: #25d366;
}

.qo-sheet__send-btn--sms {
  background-color: #334155;
}

.qo-sheet__send-options--single {
  margin-top: 0.25rem;
}

.qo-checkout-review {
  position: fixed;
  inset: 0;
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
}

.qo-checkout-review__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
}

.qo-checkout-review__panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  max-height: min(80vh, 560px);
  overflow: auto;
  background: #fff;
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.qo-checkout-review__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.qo-checkout-review__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
}

.qo-checkout-review__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border: none;
  background: #f1f5f9;
  color: #334155;
  border-radius: 0.5rem;
  cursor: pointer;
}

.qo-checkout-review__close:hover {
  background: #e2e8f0;
}

.qo-checkout-review__lines {
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
}

.qo-checkout-review__line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9375rem;
  color: #0f172a;
}

.qo-checkout-review__line-main {
  display: flex;
  gap: 0.35rem;
  min-width: 0;
}

.qo-checkout-review__qty {
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.qo-checkout-review__name {
  word-break: break-word;
}

.qo-checkout-review__line-price {
  flex-shrink: 0;
  font-weight: 600;
}

.qo-sheet__hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.qo-checkout-review__subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  border-top: 2px solid #e2e8f0;
  color: #0f172a;
}

.qo-checkout-review__fee {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.9375rem;
  color: #475569;
}

.qo-checkout-review__total-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 1.05rem;
  font-weight: 700;
  border-top: 1px solid #e2e8f0;
  color: #0f172a;
}

.qo-checkout-review__field {
  margin-bottom: 0.75rem;
}

.qo-checkout-review__confirm {
  width: 100%;
  min-height: 48px;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
}

.qo-checkout-review__confirm:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Transition */
.qo-sheet-enter-active,
.qo-sheet-leave-active {
  transition: opacity 0.2s ease;
}

.qo-sheet-enter-active .qo-sheet__panel,
.qo-sheet-leave-active .qo-sheet__panel {
  transition: transform 0.25s ease;
}

.qo-sheet-enter-from,
.qo-sheet-leave-to {
  opacity: 0;
}

.qo-sheet-enter-from .qo-sheet__panel {
  transform: translateY(100%);
}

.qo-sheet-leave-to .qo-sheet__panel {
  transform: translateY(100%);
}

@media (min-width: 768px) {
  .qo-sheet-enter-from .qo-sheet__panel {
    transform: translateY(2rem);
  }

  .qo-sheet-leave-to .qo-sheet__panel {
    transform: translateY(2rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .qo-sheet-enter-active,
  .qo-sheet-leave-active,
  .qo-sheet-enter-active .qo-sheet__panel,
  .qo-sheet-leave-active .qo-sheet__panel {
    transition: none;
  }
}
</style>
